import {
  AutoAwesomeOutlined,
  BusinessOutlined,
  LockOutlined,
  SettingsOutlined,
  ShowChartOutlined,
  SyncOutlined,
} from '@mui/icons-material';
import {
  firmDailyNavSettingsUrl,
  firmFeatureSettingsUrl,
  firmScalarAiSettingsUrl,
  firmScimConfigurationUrl,
  firmSettingsUrl,
  firmSingleSignOnUrl,
} from 'common/config/urls';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from 'layouts/FirmBaseLayout/PrimaryNavigation/PrimaryNavigation.testIds';
import { getObjectValue } from 'utilities';
import type { FirmSettingsMenuProps } from './FirmSettingsMenu.props';

const FirmSettingsMenu = ({ anchorEl, firmSlug, onClose, onNavigate, zoomLevel }: FirmSettingsMenuProps) => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  // Mirrors the Daily NAV Settings tab gate in CompanySummary: the firm-level flag alone decides visibility.
  const isDailyNavEnabled = Boolean(firmInfo?.daily_nav_enabled);

  return (
    <PrimaryNavigationMenu
      id={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmSettingsMenu}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmSettingsMenu}
      menuMinWidth={200}
      zoomLevel={zoomLevel}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}>

      <PrimaryNavigationMenuItem
        icon={<BusinessOutlined fontSize="inherit" />}
        onClick={() => onNavigate(firmSettingsUrl(firmSlug))}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmProfile}>
        Firm Profile
      </PrimaryNavigationMenuItem>
      <PrimaryNavigationMenuItem
        icon={<LockOutlined fontSize="inherit" />}
        onClick={() => onNavigate(firmSingleSignOnUrl(firmSlug))}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.sso}>
        Single Sign-On
      </PrimaryNavigationMenuItem>
      <PrimaryNavigationMenuItem
        icon={<SyncOutlined fontSize="inherit" />}
        onClick={() => onNavigate(firmScimConfigurationUrl(firmSlug))}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.scim}>
        SCIM
      </PrimaryNavigationMenuItem>
      {isDailyNavEnabled && (
        <PrimaryNavigationMenuItem
          icon={<ShowChartOutlined fontSize="inherit" />}
          onClick={() => onNavigate(firmDailyNavSettingsUrl(firmSlug))}
          data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.dailyNavSettings}>
          Daily NAV Settings
        </PrimaryNavigationMenuItem>
      )}
      <PrimaryNavigationMenuItem
        icon={<AutoAwesomeOutlined fontSize="inherit" />}
        onClick={() => onNavigate(firmScalarAiSettingsUrl(firmSlug))}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.scalarAiSettings}>
        Scalar AI
      </PrimaryNavigationMenuItem>
      <PrimaryNavigationMenuItem
        icon={<SettingsOutlined fontSize="inherit" />}
        onClick={() => onNavigate(firmFeatureSettingsUrl(firmSlug))}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.featureSettings}>
        Settings
      </PrimaryNavigationMenuItem>
    </PrimaryNavigationMenu>
  );
};

export default FirmSettingsMenu;
